import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import BookMyOpAPI from '../services/BookMyOpapi';
import storage from '../utils/storage';

const BookMyOpAuthContext = createContext(null);

export const useBookMyOpAuth = () => {
    const context = useContext(BookMyOpAuthContext);
    if (!context) {
        throw new Error('useBookMyOpAuth must be used within a BookMyOpAuthProvider');
    }
    return context;
};

export const BookMyOpAuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [otpState, setOtpState] = useState({
        phoneNumber: null,
        isOtpSent: false,
        attemptsRemaining: 5,
        otpError: null
    });
    
    // Add refs to prevent infinite loops
    const isFetchingRef = useRef(false);
    const lastFetchTimeRef = useRef(0);
    const fetchAttemptsRef = useRef(0);
    const maxFetchAttempts = 3;
    const minFetchInterval = 5000; // 5 seconds between fetch attempts

    const fetchUserProfile = async (forceRefresh = false) => {
        // Don't fetch if user is already loaded and not forcing refresh
        if (user && !forceRefresh) {
            console.log('Profile fetch skipped: user already loaded');
            return;
        }
        
        // Check if we have a valid token before attempting to fetch
        // Try sync first, then async for mobile
        let accessToken = storage.getItemSync('bookmyop_access_token');
        if (!accessToken) {
            // Try async read for mobile (Capacitor Preferences)
            try {
                accessToken = await storage.getItem('bookmyop_access_token');
            } catch (err) {
                console.error('Error reading access token:', err);
            }
        }
        if (!accessToken) {
            console.log('Profile fetch skipped: no access token');
            setLoading(false);
            return;
        }
        
        // Prevent multiple simultaneous fetches
        if (isFetchingRef.current) {
            console.log('Profile fetch skipped: already fetching');
            return;
        }
        
        // Prevent too frequent fetches
        const now = Date.now();
        if (now - lastFetchTimeRef.current < minFetchInterval) {
            console.log('Profile fetch skipped: too frequent');
            return;
        }
        
        // Prevent too many failed attempts
        if (fetchAttemptsRef.current >= maxFetchAttempts) {
            console.log('Profile fetch skipped: too many failed attempts');
            setLoading(false);
            return;
        }
        
        isFetchingRef.current = true;
        lastFetchTimeRef.current = now;
        
        try {
            console.log('Fetching user profile...');
            const response = await BookMyOpAPI.getProfile();
            if (response.status === 'success' && response.data) {
                setUser(response.data);
                await storage.setItem('bookmyop_user_data', response.data);
                fetchAttemptsRef.current = 0; // Reset attempts on success
                console.log('Profile fetch successful');
            }
        } catch (err) {
            console.error('Error fetching user profile:', err);
            fetchAttemptsRef.current++;
            
            // CRITICAL: Don't clear user data on profile fetch failure
            // User should stay logged in with stored data even if profile fetch fails
            // Only clear if we get a definitive auth error AND token refresh also failed
            // AND we've exhausted all retries
            // This ensures user stays logged in on refresh even if profile API is temporarily down
            if ((err.response?.status === 401 || err.isAuthError) && 
                fetchAttemptsRef.current >= maxFetchAttempts &&
                err.isAuthError) { // Only clear if token refresh also failed
                console.log('⚠️ All auth attempts failed, clearing auth data');
                setUser(null);
                await storage.removeItem('bookmyop_user_data');
                await storage.removeItem('bookmyop_access_token');
                await storage.removeItem('bookmyop_refresh_token');
            } else {
                // Keep user logged in with stored data
                // Don't clear anything - user should remain logged in
                console.log('⚠️ Profile fetch failed, but keeping user logged in with stored data');
                if (err.response?.status === 401 || err.isAuthError) {
                    console.log('Auth error detected, but keeping tokens. User stays logged in.');
                }
            }
        } finally {
            isFetchingRef.current = false;
            // Only set loading to false if it's not already set
            // (During initialization, loading is set to false after loading stored data)
            setLoading(false);
        }
    };

    useEffect(() => {
        let isMounted = true;
        
        const initializeAuth = async () => {
            console.log('🔐 Starting auth initialization...');
            
            // On native platforms, ALWAYS check Capacitor Preferences first
            // localStorage is cleared when app is closed on Android
            const isNative = Capacitor.isNativePlatform();
            
            let accessToken = null;
            let storedUserDataStr = null;
            
            if (isNative) {
                // On native: ALWAYS check Preferences first (persistent storage)
                console.log('📱 Native platform detected - checking Capacitor Preferences first...');
                try {
                    accessToken = await storage.getItem('bookmyop_access_token');
                    storedUserDataStr = await storage.getItem('bookmyop_user_data');
                    
                    console.log('📱 Preferences read:', {
                        hasToken: !!accessToken,
                        hasUserData: !!storedUserDataStr,
                        tokenLength: accessToken?.length || 0
                    });
                    
                    // If found in Preferences, also write to localStorage for faster future access
                    if (accessToken) {
                        try {
                            localStorage.setItem('bookmyop_access_token', accessToken);
                            console.log('✅ Restored token from Preferences to localStorage');
                        } catch (e) {
                            console.warn('Could not write token to localStorage:', e);
                        }
                    }
                    
                    if (storedUserDataStr) {
                        try {
                            localStorage.setItem('bookmyop_user_data', storedUserDataStr);
                            console.log('✅ Restored user data from Preferences to localStorage');
                        } catch (e) {
                            console.warn('Could not write user data to localStorage:', e);
                        }
                    }
                } catch (err) {
                    console.error('❌ Error reading from Preferences:', err);
                    // Fallback to sync read if Preferences fails
                    accessToken = storage.getItemSync('bookmyop_access_token');
                    storedUserDataStr = storage.getItemSync('bookmyop_user_data');
                }
            } else {
                // On web: Use sync read (localStorage - fast)
                console.log('🌐 Web platform - using localStorage (sync read)...');
                accessToken = storage.getItemSync('bookmyop_access_token');
                storedUserDataStr = storage.getItemSync('bookmyop_user_data');
                
                console.log('📦 Sync read:', {
                    hasToken: !!accessToken,
                    hasUserData: !!storedUserDataStr,
                    tokenLength: accessToken?.length || 0
                });
            }
            
            if (accessToken && isMounted) {
                // CRITICAL: Set axios default headers IMMEDIATELY so API calls have auth token
                // This must happen before any API calls are made
                axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
                console.log('✅ Set axios default Authorization header');
                
                // CRITICAL: Always load stored user data first for immediate UX
                // This ensures user stays logged in even if profile fetch fails
                if (storedUserDataStr) {
                    try {
                        const userData = JSON.parse(storedUserDataStr);
                        setUser(userData);
                        console.log('✅ Loaded user data from storage - user will stay logged in');
                    } catch (err) {
                        console.error('Error parsing stored user data:', err);
                    }
                }
                
                // Set loading to false immediately after loading stored data
                // This allows the app to render with stored user data
                setLoading(false);
                
                // Then try to fetch fresh profile data in the background (non-blocking)
                // Don't block the UI - user is already logged in with stored data
                fetchUserProfile(true).catch(err => {
                    console.error('Background profile fetch failed:', err);
                    // Don't clear tokens or user data - keep user logged in with stored data
                    // Only log the error - user stays logged in
                    console.log('⚠️ Profile fetch failed, but user remains logged in with stored data');
                });
            } else {
                // No token found, set loading to false immediately
                console.log('❌ No access token found, skipping auth initialization');
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        initializeAuth();
        
        return () => {
            isMounted = false;
        };
    }, []);

    const register = async (userData) => {
        try {
            setError(null);
            const response = await BookMyOpAPI.register(userData);
            
            // Store user data and token using storage utility
            await storage.setItem('bookmyop_user_data', response.data);
            await storage.setItem('bookmyop_access_token', response.data.token);
            
            setUser(response.data);
            return response;
        } catch (err) {
            setError(err.message || 'Registration failed');
            throw err;
        }
    };

    const login = async (credentials) => {
        try {
            const response = await BookMyOpAPI.login(credentials);
            
            if (response.status === 'success' && response.user) {
                setUser(response.user);
                await storage.setItem('bookmyop_user_data', response.user);
                
                // Store tokens if provided
                if (response.access_token) {
                    await storage.setItem('bookmyop_access_token', response.access_token);
                }
                if (response.refresh_token) {
                    await storage.setItem('bookmyop_refresh_token', response.refresh_token);
                }
                
                // Reset fetch attempts on successful login
                fetchAttemptsRef.current = 0;
                // Fetch fresh profile data after login
                await fetchUserProfile(true);
                return response;
            } else {
                throw new Error(response.message || 'Login failed');
            }
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    const sendOTP = async (phoneNumber) => {
        try {
            setError(null);
            setOtpState(prev => ({ ...prev, otpError: null }));
            
            console.log('Sending OTP for phone number:', phoneNumber);
            const response = await BookMyOpAPI.sendOTP(phoneNumber);
            console.log('OTP send response:', response);
            
            if (response.status === 'success') {
                setOtpState(prev => ({
                    ...prev,
                    phoneNumber,
                    isOtpSent: true,
                    attemptsRemaining: response.attempts_remaining || prev.attemptsRemaining
                }));
                return response;
            } else {
                const errorMsg = response.message || 'Failed to send OTP';
                console.error('OTP send failed:', errorMsg);
                throw new Error(errorMsg);
            }
        } catch (err) {
            console.error('OTP send error in context:', err);
            const errorMessage = err.message || err.originalError?.message || 'Failed to send OTP. Please check your connection and try again.';
            setOtpState(prev => ({ 
                ...prev, 
                otpError: errorMessage,
                attemptsRemaining: err.response?.data?.attempts_remaining || err.originalError?.response?.data?.attempts_remaining || prev.attemptsRemaining
            }));
            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    const verifyOTP = async (otp) => {
        try {
            setError(null);
            setOtpState(prev => ({ ...prev, otpError: null }));
            
            if (!otpState.phoneNumber) {
                throw new Error('Phone number not provided. Please request OTP first.');
            }

            const response = await BookMyOpAPI.verifyOTP(otpState.phoneNumber, otp);
            
            if (response.status === 'success' && response.user) {
                // Store user data and tokens using storage utility
                // CRITICAL: Save to both Preferences (persistent) and localStorage (fast access)
                console.log('💾 Saving authentication data after OTP verification...');
                
                await storage.setItem('bookmyop_user_data', response.user);
                if (response.access_token) {
                    await storage.setItem('bookmyop_access_token', response.access_token);
                    // CRITICAL: Set axios default header immediately
                    axios.defaults.headers.common['Authorization'] = `Bearer ${response.access_token}`;
                    console.log('✅ Access token saved and axios header set');
                    // Verify it was saved
                    const verifyToken = await storage.getItem('bookmyop_access_token');
                    if (verifyToken === response.access_token) {
                        console.log('✅ Access token verified in storage');
                    } else {
                        console.error('❌ Access token verification failed!');
                    }
                }
                if (response.refresh_token) {
                    await storage.setItem('bookmyop_refresh_token', response.refresh_token);
                    // Verify it was saved
                    const verifyRefresh = await storage.getItem('bookmyop_refresh_token');
                    if (verifyRefresh === response.refresh_token) {
                        console.log('✅ Refresh token verified in storage');
                    } else {
                        console.error('❌ Refresh token verification failed!');
                    }
                }
                setUser(response.user);
                
                // Reset fetch attempts on successful OTP verification
                fetchAttemptsRef.current = 0;
                
                // Fetch fresh profile data after OTP verification
                await fetchUserProfile(true);
                
                // Reset OTP state
                setOtpState({
                    phoneNumber: null,
                    isOtpSent: false,
                    attemptsRemaining: 5,
                    otpError: null
                });
                
                return response;
            } else {
                throw new Error(response.message || 'OTP verification failed');
            }
        } catch (err) {
            setOtpState(prev => ({ ...prev, otpError: err.message }));
            throw err;
        }
    };

    const resetOTPState = () => {
        setOtpState({
            phoneNumber: null,
            isOtpSent: false,
            attemptsRemaining: 5,
            otpError: null
        });
    };

    const logout = async () => {
        await BookMyOpAPI.logout();
        setUser(null);
        setOtpState({
            phoneNumber: null,
            isOtpSent: false,
            attemptsRemaining: 5,
            otpError: null
        });
        await storage.removeItem('bookmyop_user_data');
        await storage.removeItem('bookmyop_access_token');
        // Reset fetch state on logout
        isFetchingRef.current = false;
        fetchAttemptsRef.current = 0;
        lastFetchTimeRef.current = 0;
    };

    // Check if user has valid stored authentication data
    const hasStoredAuth = () => {
        // Check sync storage first (localStorage)
        const accessToken = storage.getItemSync('bookmyop_access_token');
        const userData = storage.getItemSync('bookmyop_user_data');
        return !!(accessToken && userData);
    };

    // Extract location data from user profile
    const userLocation = user ? {
        latitude: user.latitude,
        longitude: user.longitude,
        city: user.current_city,
        state: user.current_state,
        pincode: user.current_pincode,
        updatedAt: user.location_updated_at
    } : null;

    const value = {
        user,
        loading,
        error,
        otpState,
        userLocation,
        register,
        login,
        logout,
        sendOTP,
        verifyOTP,
        resetOTPState,
        isAuthenticated: !!user,
        hasStoredAuth,
        refreshProfile: fetchUserProfile
    };

    return (
        <BookMyOpAuthContext.Provider value={value}>
            {children}
        </BookMyOpAuthContext.Provider>
    );
};

export default BookMyOpAuthContext; 